import { Core } from "@faire/design-tokens";
import { TOP_SELLERS_CATEGORY_URL_ID } from "@faire/retailer-shared/categories/urlId";
import { getSessionCountry } from "@faire/retailer-visitor-shared/serialized-data/getSessionCountry";
import { optimizeImageUrlFillSquare } from "@faire/web/common/images/optimizeImages";
import {
  formatMoney,
  RoundingStrategy,
  selectLocalCurrency,
} from "@faire/web/common/money";
import { useIsSSR } from "@faire/web/common/server-side-rendering/isSSR";
import { Button } from "@faire/web/slate/Button";
import { Column, Flex } from "@faire/web/slate/Layout";
import { Typography } from "@faire/web/slate/Typography";
import { useDoActionWhenVisible } from "@faire/web/ui/hooks/useDoActionWhenVisible";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { ResponsiveContentV2 } from "@faire/web/ui/ResponsiveContentV2";
import { trackLoggedOutRetailersLoggedOutHomeRedesignCtaClick } from "@faire/web-api--source/events/loggedOutRetailers/click/loggedOutHomeRedesignCta";
import { trackLoggedOutRetailersLoggedOutHomeRedesignView } from "@faire/web-api--source/events/loggedOutRetailers/view/loggedOutHomeRedesign";
import { IFaireMoney } from "@faire/web-api--source/indigofair/data/IFaireMoney";
import { IImage } from "@faire/web-api--source/indigofair/data/IImage";
import { ISO3166Alpha3 } from "@faire/web-api--source/indigofair/iso3166/ISO3166Alpha3";
import categoryPath from "@faire/web-api--source/routes/www/category/c1CategoryUrlId";
import React, { useMemo } from "react";

const BESTSELLERS_BANNER_IMAGES = [
  "https://cdn.faire.com/fastly/2bc4d97018ef06e79731c8fbbfa201b4a440837c450a5725d323c344f4676645.jpeg",
  "https://cdn.faire.com/fastly/f28fda81e75209633f57367c1f0a46f1fe6148b0001dc50ca12e751aa2ae9811.jpeg",
  "https://cdn.faire.com/fastly/4fa9be357b08c6ee07517a66256380f0315bf38ceb05c0bf969dff9f548c4b0c.jpeg",
  "https://cdn.faire.com/fastly/9d389feb08a0d51bb991417050e2e120cf536e20785109edecf87fa7c7758b73.jpeg",
  "https://cdn.faire.com/fastly/711f60feb020f139346b5cd15b69422bc09f380e80d2bfdbccfd67a8c42794cb.jpeg",
  "https://cdn.faire.com/fastly/6ff47a25ba068d16f90a2c5f5227f20d6600dfb782c4f749a8554915adcf0482.jpeg",
];

export const BestsellersBanner: React.FC = () => {
  const ref = useDoActionWhenVisible(() =>
    trackLoggedOutRetailersLoggedOutHomeRedesignView("bestsellers_banner", "v1")
  );
  return (
    <Flex
      direction={{ mobileAndAbove: "column", tabletAndAbove: "row" }}
      gap={{ mobileAndAbove: "4x", tabletAndAbove: "5x" }}
      style={{ backgroundColor: "#5b2a25" }}
      className="w-full px-8 py-12 lg:px-14 lg:py-[72px] xl:px-14 xl:py-[88px]"
      data-test-id="bestsellers-banner"
      ref={ref}
    >
      <ResponsiveContentV2
        desktop={<BestsellersCTA />}
        tabletAndBelow={<BestsellersCTA />}
      />
      <div className="grid aspect-square h-full w-full grid-cols-2 grid-rows-2 justify-center gap-4 [grid-template-areas:'img0_img1''img2_img3'] xl:aspect-auto xl:grid-cols-[260px_260px_260px_260px] xl:grid-rows-[260px_260px] xl:[grid-template-areas:'cta_cta_img0_img1''img2_img3_img4_img5']">
        <ResponsiveContentV2 xLargeAndAbove={<BestsellersCTA />} />

        {BESTSELLERS_BANNER_IMAGES.map((imageUrl, index) => (
          <BestsellersImage imageUrl={imageUrl} index={index} key={index} />
        ))}
      </div>
    </Flex>
  );
};

const BestsellersCTA: React.FC = () => {
  const sessionCountry = getSessionCountry();
  const headerIncentive = useMemo(
    () =>
      formatMoney({
        money: IFaireMoney.build({
          amount_cents: 100_00,
          currency: selectLocalCurrency(sessionCountry ?? ISO3166Alpha3.USA),
        }),
        roundingStrategy: RoundingStrategy.DROP_CENTS,
        disableAutoConvertCurrency: true,
      }),
    [sessionCountry]
  );
  return (
    <Column gap="2x" className="col-[cta] self-center md:max-w-[450px]">
      <Typography
        variant={{
          mobileAndAbove: "displaySSerifRegular",
          desktopAndAbove: "displayMSerifRegular",
        }}
        color={Core.text.inverse}
      >
        <StrictLocalMsg
          defaultMessage="Discover your next bestseller"
          description={{
            text: "Logged out home page bestseller banner title",
            img: "https://cdn.faire.com/fastly/03e9d7d6722745f465d468459dd2ef44b8056bfeb4453997ad0f25331b0498c9.png",
          }}
        />
      </Typography>
      <Typography variant="subheadingSansRegular" color={Core.text.inverse}>
        <StrictLocalMsg
          defaultMessage="See what retailers from across the globe are loving on Faire"
          description={{
            text: "Logged out home page bestseller banner content",
            img: "https://cdn.faire.com/fastly/03e9d7d6722745f465d468459dd2ef44b8056bfeb4453997ad0f25331b0498c9.png",
          }}
          values={{ headerIncentiveMoney: headerIncentive }}
        />
      </Typography>
      <Button
        style={{
          width: "fit-content",
          color: Core.text.inverse,
          borderColor: Core.text.inverse,
          backgroundColor: "transparent",
        }}
        className="hover:bg-core-surface-inverse! hover:text-[#5b2a25]!"
        variant="secondary"
        linkTo={categoryPath(TOP_SELLERS_CATEGORY_URL_ID)}
        onClick={() => {
          trackLoggedOutRetailersLoggedOutHomeRedesignCtaClick(
            "bestsellers_banner-cta",
            categoryPath(TOP_SELLERS_CATEGORY_URL_ID),
            "v1"
          );
        }}
      >
        <StrictLocalMsg
          defaultMessage="Shop all bestsellers"
          description={{
            text: "Logged out home page bestseller banner cta button text",
            img: "https://cdn.faire.com/fastly/03e9d7d6722745f465d468459dd2ef44b8056bfeb4453997ad0f25331b0498c9.png",
          }}
        />
      </Button>
    </Column>
  );
};

const BestsellersImage: React.FC<{
  imageUrl: string;
  index: number;
}> = ({ imageUrl, index }) => {
  const isSSR = useIsSSR();
  const optimizedImageUrl = optimizeImageUrlFillSquare(
    IImage.build({
      url: imageUrl,
    }),
    isSSR,
    260
  );
  return (
    <img
      src={optimizedImageUrl}
      loading="lazy"
      style={{ gridArea: `img${index}`, height: "100%", width: "100%" }}
      data-index={index}
      className={index === 4 || index === 5 ? "hidden xl:block" : ""}
    />
  );
};
